import { arrayRemove, arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { getId, getInstallations } from 'firebase/installations';
import {
  isSupported,
  onMessage,
  onRegistered,
  onUnregistered,
  register,
  unregister,
  type MessagePayload,
} from 'firebase/messaging';
import { app, db, messaging } from '@/lib/firebase';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string,
};

let currentUid: string | null = null;
let fcmRegistration: ServiceWorkerRegistration | null = null;
let registerMessagingPromise: Promise<void> | null = null;

export const notificationService = {
  async isSupported(): Promise<boolean> {
    if (typeof window === 'undefined' || !('Notification' in window)) return false;
    return isSupported();
  },

  async getPermission(): Promise<NotificationPermission | null> {
    if (typeof window === 'undefined' || !('Notification' in window)) return null;
    return Notification.permission;
  },

  async requestPermission(): Promise<NotificationPermission | null> {
    if (typeof window === 'undefined' || !('Notification' in window)) return null;
    return Notification.requestPermission();
  },

  async registerMessaging(uid: string): Promise<void> {
    if (typeof window === 'undefined') throw new Error('FCM is only available in the browser');
    if (registerMessagingPromise) return registerMessagingPromise;

    registerMessagingPromise = (async () => {
      if (!(await this.isSupported())) throw new Error('FCM is not supported in this browser');
      if (!messaging) throw new Error('FCM messaging is not initialized');
      if (!VAPID_KEY) {
        console.warn('VITE_FIREBASE_VAPID_KEY is not set; cannot register for FCM.');
        throw new Error('VITE_FIREBASE_VAPID_KEY is not set');
      }

      currentUid = uid;
      fcmRegistration = await navigator.serviceWorker.register(
        `/fcm/firebase-messaging-sw.js?${new URLSearchParams(FIREBASE_CONFIG)}`,
        { scope: '/fcm/' }
      );
      // `register()` skips the script re-fetch when the last update check is
      // under ~24h old; `update()` forces it so new service worker versions
      // (which carry notification copy) roll out on the next app open.
      await fcmRegistration.update();
      await register(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: fcmRegistration,
      });

      // `onRegistered` only fires when the FID changes, so it is skipped when the
      // SDK considers this instance already registered (e.g. after a reinstall
      // where Firestore still holds a stale FID). Persist the current FID here so
      // the user document always contains a deliverable target.
      const fid = await getId(getInstallations(app));
      await this.saveFid(uid, fid);
    })().finally(() => {
      registerMessagingPromise = null;
    });

    return registerMessagingPromise;
  },

  async unregisterMessaging(uid: string): Promise<void> {
    if (!messaging) return;
    try {
      const fid = await getId(getInstallations(app));
      await this.removeFid(uid, fid);
    } catch (err) {
      console.warn('Failed to remove FID during deregistration:', err);
    }
    currentUid = null;
    await unregister(messaging);
  },

  async refreshRegistration(uid: string): Promise<void> {
    const permission = await this.getPermission();
    if (permission !== 'granted') return;
    const existing = fcmRegistration ?? (await navigator.serviceWorker.getRegistration('/fcm/'));
    if (existing?.active) {
      fcmRegistration = existing;
      await existing.update();
      return;
    }
    await this.registerMessaging(uid);
  },

  async saveFid(uid: string, fid: string): Promise<void> {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, { fids: arrayUnion(fid) });
  },

  async removeFid(uid: string, fid: string): Promise<void> {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, { fids: arrayRemove(fid) });
  },

  async dismissNotificationsForConversation(conversationId: string): Promise<void> {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    const registration =
      fcmRegistration ?? (await navigator.serviceWorker.getRegistration('/fcm/'));
    if (!registration) return;
    const notifications = await registration.getNotifications({ tag: conversationId });
    notifications.forEach(notification => notification.close());
  },

  onForegroundMessage(handler: (payload: MessagePayload) => void): () => void {
    if (!messaging) return () => {};
    const unsubscribe = onMessage(messaging, handler);
    return unsubscribe;
  },
};

if (messaging) {
  onRegistered(messaging, async fid => {
    if (!currentUid) return;
    await notificationService.saveFid(currentUid, fid);
  });

  onUnregistered(messaging, async fid => {
    if (!currentUid) return;
    await notificationService.removeFid(currentUid, fid);
  });
}
